import { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useDetailedCaseContext } from '../DetailedCaseProvider';
import {
  applyEventFilter,
  commentEventFilter,
  EventFilter,
  externalAdminEventFilter,
  rideUpdatesEventFilter,
} from './useFilteredEvents';
import useMe from 'contexts/authentication/useMe';
import Roles from 'constants/Roles';
import { AssignmentModel, CaseEventType, EventModel } from 'api/model';
import Select from 'components/inputs/Select';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 5px 0;
  gap: 5px;
`;

const FilterPill = styled.button<{
  active?: boolean;
  alert?: boolean;
  filterType?: 'comment' | 'rideUpdate';
}>`
  padding: 2px 10px;

  --foreground-color: ${({ theme, active, alert, filterType }) => {
    if (active) return theme.colors.foreground.button;
    if (alert)
      switch (filterType) {
        case 'rideUpdate':
          return theme.colors.foreground.error;
        case 'comment':
          return theme.colors.foreground.newMessage;
        default:
          break;
      }

    return theme.colors.foreground.primary;
  }};
  --background-color: ${({ theme, active, alert, filterType }) => {
    if (active) {
      if (alert) {
        switch (filterType) {
          case 'rideUpdate':
            return theme.colors.foreground.error;
          case 'comment':
            return theme.colors.foreground.newMessage;
          default:
            break;
        }
      }
      return theme.colors.background.button;
    }
    return theme.colors.background.primary;
  }};

  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  border: 1px solid
    ${({ theme, active }) =>
      active ? 'var(--background-color)' : theme.colors.background.button};
  background: var(--background-color);
  color: ${({ active }) => (active ? css`var(--foreground-color)` : 'inherit')};
  font: inherit;
  font-size: ${({ theme }) => theme.sizes.font.small};
  font-weight: ${({ alert, active }) => (alert && active ? '600' : 'inherit')};
  cursor: pointer;
`;

const AssignmentSelect = styled(Select)`
  font-size: ${({ theme }) => theme.sizes.font.small};
  padding: 2px 6px;
  height: auto;
  width: auto;
  max-width: 100%;
  margin-left: auto;
`;

const NotifyCircle = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 1.5em;
  min-height: 1.5em;
  padding: 2px;
  border-radius: 1000px;
  background-color: var(--foreground-color);
  color: var(--background-color);
  font-size: 0.8em;
  font-weight: 600;
`;

interface Props {
  allEvents: EventModel[];
  selectedFilter: EventFilter | null;
  onSelectFilter(filter: EventFilter | null): void;
  assignments: AssignmentModel[];
  selectedAssignmentId: number | null;
  onSelectAssignmentId(assignmentId: number | null): void;
}

const formatAssignmentLabel = (
  assignment: AssignmentModel,
  index: number
): string => {
  const fromCity = assignment.fromCity?.trim();
  const toCity = assignment.toCity?.trim();
  const route =
    fromCity && toCity
      ? ` — ${fromCity} → ${toCity}`
      : fromCity
        ? ` — ${fromCity}`
        : toCity
          ? ` — ${toCity}`
          : '';
  return `Uppdrag ${index + 1}${route}`;
};

const EventsFilterPicker: FC<Props> = ({
  allEvents,
  selectedFilter,
  onSelectFilter,
  assignments,
  selectedAssignmentId,
  onSelectAssignmentId,
}) => {
  const me = useMe();
  const isExternalAdmin = me?.roles.includes(Roles.ExternalAdmin);
  const { caseResponse } = useDetailedCaseContext();

  const assignmentOptions = useMemo(
    () =>
      assignments.map((assignment, index) => ({
        id: assignment.assignmentID,
        label: formatAssignmentLabel(assignment, index),
      })),
    [assignments]
  );

  return (
    <Wrapper>
      <FilterPill
        active={selectedFilter === null}
        onClick={() => onSelectFilter(null)}
      >
        Alla
      </FilterPill>

      <FilterPill
        active={selectedFilter === commentEventFilter}
        alert={caseResponse.response?.case.adminHasUnreadComments}
        filterType="comment"
        onClick={() => onSelectFilter(commentEventFilter)}
      >
        {commentEventFilter.name}{' '}
        {caseResponse.response?.case.adminHasUnreadComments &&
          applyEventFilter(allEvents, commentEventFilter).filter(
            (e) => !e.readByAdmin
          ).length > 0 && (
            <NotifyCircle>
              {
                applyEventFilter(allEvents, commentEventFilter).filter(
                  (e) => !e.readByAdmin
                ).length
              }
            </NotifyCircle>
          )}
      </FilterPill>

      <FilterPill
        active={selectedFilter === rideUpdatesEventFilter}
        alert={caseResponse.response?.case.newDataFromRide}
        filterType="rideUpdate"
        onClick={() => onSelectFilter(rideUpdatesEventFilter)}
      >
        {rideUpdatesEventFilter.name}{' '}
        {caseResponse.response?.case.newDataFromRide && (
          <NotifyCircle>
            {applyEventFilter(allEvents, rideUpdatesEventFilter).length}
          </NotifyCircle>
        )}
      </FilterPill>

      {!isExternalAdmin && (
        <FilterPill
          active={selectedFilter === externalAdminEventFilter}
          alert={caseResponse.response?.case.newDataFromRide}
          filterType="rideUpdate"
          onClick={() => onSelectFilter(externalAdminEventFilter)}
        >
          {externalAdminEventFilter.name}{' '}
          {caseResponse.response?.case.adminHasUnreadComments &&
            (caseResponse.response?.events?.filter(
              (e) =>
                e.caseEventType === CaseEventType.ExternalAdminChange &&
                !e.readByAdmin
            ).length ?? 0) > 0 && (
              <NotifyCircle>
                {
                  applyEventFilter(allEvents, externalAdminEventFilter).filter(
                    (e) => !e.readByAdmin
                  ).length
                }
              </NotifyCircle>
            )}
        </FilterPill>
      )}

      {assignmentOptions.length > 0 && (
        <AssignmentSelect
          small
          value={selectedAssignmentId ?? ''}
          onChange={(e) =>
            onSelectAssignmentId(
              e.target.value === '' ? null : Number(e.target.value)
            )
          }
          aria-label="Filtrera händelser per uppdrag"
        >
          <option value="">Alla uppdrag</option>
          {assignmentOptions.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </AssignmentSelect>
      )}
    </Wrapper>
  );
};

export default EventsFilterPicker;
